import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  DollarSign,
  Clock,
  CheckCircle,
  TrendingUp,
  Activity,
  Award,
  Calendar,
  CreditCard
} from 'lucide-react';

interface DashboardData {
  userId: string;
  stats: {
    totalEarned: string;
    pendingEarnings: string;
    completedTasks: number;
    pendingReview: number;
    tasksInProgress: number;
  };
  recentTasks: Array<{
    id: string;
    description: string;
    reward: string;
    status: string;
    claimedAt: string;
    completedAt?: string;
    paidAt?: string;
  }>;
  recentPayments: Array<{
    task_id: string;
    amount: string;
    paid_at: string;
  }>;
  chartData: {
    daily: Array<{ date: string; amount: string }>;
    weekly: {
      thisWeek: { count: number; earnings: number };
      lastWeek: { count: number; earnings: number };
    };
  };
}

export default function TaskTapDashboard() {
  const params = useParams();
  const userId = params.userId;
  const hash = params['*']; // Captures the hash after the userId
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'payments'>('overview');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      fetchDashboard();
      const interval = setInterval(fetchDashboard, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [userId, hash]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(
        `https://futr-bet-api.b-9f2.workers.dev/tasktap/dashboard/${userId}/${hash || ''}`
      );
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
        setError(null);
      } else if (response.status === 401) {
        setError('Unauthorized - Please access your dashboard through the link in Slack');
        setDashboard(null);
      } else {
        setError('Failed to load dashboard');
        setDashboard(null);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      setError('Network error - please try again');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading dashboard...</div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">
            {error || 'Dashboard not available'}
          </div>
          {error?.includes('Unauthorized') && (
            <div className="text-gray-400 mt-4">
              <p>For security, dashboards can only be accessed through Slack.</p>
              <p className="mt-2">Check your Slack DMs for your personal dashboard link.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'claimed': return 'bg-blue-500/20 text-blue-400';
      case 'pending_review': return 'bg-yellow-500/20 text-yellow-400';
      case 'paid': return 'bg-green-500/20 text-green-400';
      case 'needs_revision': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'claimed': return <Clock className="w-4 h-4" />;
      case 'pending_review': return <Activity className="w-4 h-4" />;
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-8 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">TaskTap Dashboard</h1>
              <p className="text-gray-400 mt-1">Worker ID: {userId}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-gray-400">Total Earned</div>
                <div className="text-2xl font-bold text-green-400">
                  {dashboard.stats.totalEarned}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-400">Pending</div>
                <div className="text-2xl font-bold text-yellow-400">
                  {dashboard.stats.pendingEarnings}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-green-400" />
              <div className="text-2xl font-bold">{dashboard.stats.totalEarned}</div>
            </div>
            <div className="text-sm text-gray-400">Total Earned</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-yellow-400" />
              <div className="text-2xl font-bold">{dashboard.stats.pendingEarnings}</div>
            </div>
            <div className="text-sm text-gray-400">Pending Review</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-blue-400" />
              <div className="text-2xl font-bold">{dashboard.stats.completedTasks}</div>
            </div>
            <div className="text-sm text-gray-400">Completed</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-8 h-8 text-purple-400" />
              <div className="text-2xl font-bold">{dashboard.stats.tasksInProgress}</div>
            </div>
            <div className="text-sm text-gray-400">In Progress</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-cyan-400" />
              <div className="text-2xl font-bold">
                ${dashboard.chartData.weekly.thisWeek.earnings.toFixed(2)}
              </div>
            </div>
            <div className="text-sm text-gray-400">This Week</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          {(['overview', 'tasks', 'payments'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Earnings Chart */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Daily Earnings (Last 7 Days)</h2>
              <div className="space-y-3">
                {dashboard.chartData.daily.map((day, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="text-sm text-gray-400">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="h-6 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{
                            width: `${Math.min(100, (parseFloat(day.amount) / 50) * 100)}%`
                          }}
                        />
                      </div>
                    </div>
                    <div className="text-sm font-mono">${day.amount}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Weekly Comparison */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Weekly Comparison</h2>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">This Week</span>
                    <span className="font-bold text-green-400">
                      ${dashboard.chartData.weekly.thisWeek.earnings.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {dashboard.chartData.weekly.thisWeek.count} tasks completed
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Last Week</span>
                    <span className="font-bold text-gray-400">
                      ${dashboard.chartData.weekly.lastWeek.earnings.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {dashboard.chartData.weekly.lastWeek.count} tasks completed
                  </div>
                </div>

                {dashboard.chartData.weekly.thisWeek.earnings > dashboard.chartData.weekly.lastWeek.earnings && (
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <div className="flex items-center gap-2 text-green-400">
                      <TrendingUp className="w-5 h-5" />
                      <span>
                        Up {((dashboard.chartData.weekly.thisWeek.earnings / Math.max(1, dashboard.chartData.weekly.lastWeek.earnings) - 1) * 100).toFixed(1)}% from last week!
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Recent Tasks</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4">Task</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Reward</th>
                    <th className="text-left py-3 px-4">Claimed</th>
                    <th className="text-left py-3 px-4">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recentTasks.map(task => (
                    <tr key={task.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="py-3 px-4">
                        <div className="max-w-xs truncate">{task.description}</div>
                        <div className="text-xs text-gray-500 mt-1">{task.id.slice(0, 8)}...</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {getStatusIcon(task.status)}
                          <span>{task.status.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono">{task.reward}</td>
                      <td className="py-3 px-4 text-sm text-gray-400">
                        {task.claimedAt ? new Date(task.claimedAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-400">
                        {task.completedAt ? new Date(task.completedAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Payment History</h2>
            {dashboard.recentPayments.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recentPayments.map((payment, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-green-400" />
                      <div>
                        <div className="font-medium">Payment Received</div>
                        <div className="text-sm text-gray-400">
                          {new Date(payment.paid_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-xl font-bold text-green-400">
                      {payment.amount}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No payments yet. Complete tasks to earn!
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}